<template>
  <div v-if="currentStandard && currentStandard.Id">
    <div id="assessment-standard" class="assessment-standard">
      <div class="form-row cta-group">
        <div class="back-assessment-link">
          <a role="navigation" href="#" @click="routeToSummary()" @keydown.enter="routeToSummary()" class="back-assessment-link"
            ><span class="angle-circle-left-green"></span>
            <div>Back to <strong>Start assessment</strong></div></a
          >
        </div>
      </div>
      <h1 class="page-title">{{ currentAssessment.Name }}</h1>

      <div class="form-row pb-5">
        <div class="col-12 d-md-flex flex-md-row flex-column">
          <div class="label-goto align-self-center mr-2">Go to:</div>
          <div class="select-field-2">
            <select 
              @change="routeToStandardDropdown($event)"
              v-model="selectedStandard"
            >
              <option
                v-for="standard in currentAssessment.Standards"
                :key="'dropdown' + standard.Id"
                :selected="standard.Id == currentStandard.Id"
                :value="standard.Id"
                >{{ standard.Name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="form-row">
        <div class="p-0 col-12 col-lg-4" v-if="currentStandard.Image">
          <img class="img-fluid" :src="currentStandard.Image" :alt="currentStandard.AlternativeText"/>
        </div>
        <div class="p-0 col-12 col-lg-8">
          <div class="standard-container">
            <h4>{{ currentStandard.Name }}</h4>
            <div
              class="html-content"
              v-html="currentStandard.StandardIntroduction"
            ></div>
          </div>
        </div>
      </div>

      <div>
        <!-- SECTIONS -->
        <div
          class="assessment-section"
          v-for="(section, sectionIndex) in currentStandard.Sections"
          :key="section.Id"
        >
          <div v-if="section.Questions">
            <div class="form-row my-3">
              <div class="col-6">
                <div class="name">
                  {{ section.Name }}
                </div>
              </div>
              <div class="col-6">
                <div class="count">
                  {{ section.Questions.length }} question{{
                    section.Questions.length === 1 ? "" : "s"
                  }}
                </div>
              </div>
            </div>
            <!-- QUESTIONS -->
            <div class="list-group">
              <div
                v-for="(question, questionIndex) in section.Questions"
                :key="question.Id"
              >
                <AssessmentQuestion
                  :question="question"
                  :questionNumber="questionNumber(sectionIndex, questionIndex)"
                  @inputChanged="inputChangedInQuestion"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-row buttons">
        <div class="col-12 col-md-3">
          <a role="navigation" href="#" @click="routeToSummary()" @keydown.enter="routeToSummary()"  class="back-link">
            <span class="angle-right-green mr-2"></span>Go to summary
          </a>
        </div>
        <div class="col-12 col-md-4">
          <a role="navigation" href="#" @click="routeToActionPlan()" @keydown.enter="routeToActionPlan()" class="back-link">
            <span class="angle-right-green mr-2"></span>View action plan
          </a>
        </div>
        <div class="col-12 col-md-5">
          <a role="button"
            v-show="!showSavedTick"
            :class="{ disabled: !this.unsavedChanges }"
            @click="saveAssessments()"
            @keydown.enter="saveAssessments()"
            class="cta-white-193 float-md-right"
            tabindex="0"
          >
            Save progress<span class="angle-right-white"></span>
          </a>
          <a v-show="showSavedTick" class="cta-white-193 float-md-right">
            <span class="button-tick"></span>
          </a>
        </div>
      </div>

      <div role="navigation" class="pagination">
        <a  v-show="currentStandardIndex > 0" @click="routeToPreviousStandard()" @keydown.enter="routeToPreviousStandard()"
          tabindex="0"><span class="angle-circle-left-grey"></span
        ></a>
        <a 
          class="px-2"
          v-for="(standard, index) in currentAssessment.Standards"
          :aria-label="`page ${index + 1}`"
          :key="'nav' + standard.Id"
          :class="{ active: standard.Id === currentStandard.Id }"
          @click="routeToStandardBottomNav(standard.Id)"
          @keydown.enter="routeToStandardBottomNav(standard.Id)"
          tabindex="0"
        >
          {{ index + 1 }}
        </a>
        <a
          v-show="currentStandardIndex + 1 < currentAssessment.Standards.length"
          @click="routeToNextStandard()"
          @keydown.enter="routeToNextStandard()"
          tabindex="0"
          ><span class="angle-circle-right-grey"></span
        ></a>
      </div>
      <div class="space"></div>
      <div>
        <AssessmentModal
          v-show="showModal"
          :isOpen="showModal"
          @handleDontSave="dontSave"
          @handleSave="saveAndContinue"
          @handleCancel="closeModal"
        />
      </div>
      <popup-modal ref="popup">
        <p>
          You have not saved changes to your account. If you don’t save them
          before leaving the page, you will lose them.
        </p>
        <p>Are you sure you want to leave?</p>
        <div class="d-flex">
          <a tabindex="0" role="button" class="cancel mr-auto my-auto" @click="cancelPopup" @keydown.enter="cancelPopup">Cancel</a>
          <button role="button" class="cta-green-169" @click="confirmPopup" @keydown.enter="confirmPopup">
            OK<span class="angle-right-green"></span>
          </button>
        </div>
      </popup-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import {
  GET_SATCONTENTACTION,
  GET_SATSAVEDDATAACTION
} from "../store/mutation-types";
import AssessmentQuestion from "@/components/assessment-question";
import AssessmentModal from "@/components/assessment-modal";
import { privateRouter } from "../router";
import PopupModal from "../components/popup-modal";

export default {
  name: "AssessmentStandard",
  data() {
    return {
      selectedStandard: this.standardId,
      //unsavedChanges: false,
      showModal: false,
      selectedRoute: "",
      showSavedTick: false
    };
  },
  components: {
    AssessmentQuestion,
    AssessmentModal,
    PopupModal
  },
  props: {
    satId: {
      type: Number,
      default: () => 0
    },
    standardId: {
      type: Number,
      default: () => 0
    }
  },
  beforeMount() {
    window.addEventListener("beforeunload", this.checkUnsavedChanges);
  },
  async mounted() {
    //await this.loadSatContent(); // loading again here triggers unsaved changes flag set to true
  },
  async created() {
    //await this.loadSatContent();
    /* this.currentStandard = this.getCurrentStandard(); */
  },
  methods: {
    ...mapActions([
      GET_SATCONTENTACTION,
      GET_SATSAVEDDATAACTION,
      "saveAssessmentsAction",
      "setUnsavedChangesAction"
    ]),
    async loadSatContent() {
      //if (!this.satContent || !this.satContent.Tools || !this.currentStandard) {
      // get satContent from API if not in the store (e.g. hitting this route before overview page)
      await this.getSatContentAction();
      //}
    },
    checkUnsavedChanges(event) {
      if (!this.unsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    },
    /* getCurrentStandard() {
                let allStandards = [];
                let standard = {};
                if(this.satContent && this.satContent.Tools && this.satId && this.standardId) {
                    this.satContent.Tools.forEach(tool => {
                        allStandards = allStandards.concat(tool.Standards);
                    });
                    standard = allStandards.filter((standard) => standard.Id === this.standardId)[0]
                }
                return standard;
            }, */
    routeToSummary() {
      if (this.unsavedChanges) {
        this.selectedRoute = "routeToSummary";
        this.showModal = true;
      } else {
        privateRouter.push({
          name: "AssessmentStart",
          query: {
            satId: this.satId
          }
        });
      }
    },
    routeToActionPlan() {
      if (this.unsavedChanges) {
        this.selectedRoute = "routeToActionPlan";
        this.showModal = true;
      } else {
        privateRouter.push({
          name: "ActionPlan",
          query: {
            satId: this.satId
          }
        });
      }
    },
    routeToStandardDropdown(event) {
      privateRouter.push({
        name: "AssessmentStandard",
        query: {
          satId: this.satId,
          standardId: event.target.value
        }
      });
    },
    routeToStandardBottomNav(standardId) {
      this.selectedStandard = standardId;
      privateRouter.push({
        name: "AssessmentStandard",
        query: {
          satId: this.satId,
          standardId: standardId
        }
      });
    },
    routeToPreviousStandard() {
      if (this.currentStandardIndex > 0) {
        this.routeToStandardBottomNav(
          this.currentAssessment.Standards[this.currentStandardIndex - 1].Id
        );
      }
    },
    routeToNextStandard() {
      if (
        this.currentStandardIndex + 1 <
        this.currentAssessment.Standards.length
      ) {
        this.routeToStandardBottomNav(
          this.currentAssessment.Standards[this.currentStandardIndex + 1].Id
        );
      }
    },
    async saveAssessments() {
      this.showSavedTick = true;
      await this.saveAssessmentsAction(this.satContent);
      this.showSavedTick = false;
      this.unsavedChanges = false;
    },
    inputChangedInQuestion() {
      this.unsavedChanges = true;
    },
    async dontSave() {
      await this.getSatContentAction(); // get from DB to clear satContent changes
      this.unsavedChanges = false;
      this[this.selectedRoute]();
    },
    async saveAndContinue() {
      await this.saveAssessments();
      this[this.selectedRoute]();
    },
    closeModal() {
      this.selectedRoute = "";
      this.showModal = false;
    },
    questionNumber(sectionIndex, questionIndex) {
      let questionNumber = 0;
      for (let index = 0; index < sectionIndex; index++) {
        const section = this.currentStandard.Sections[index];
        questionNumber = questionNumber + section.Questions.length;
      }
      return questionNumber + questionIndex + 1;
    },
    showPopup() {
      this.$refs.popup.open();
      // Return promise so the caller can get results
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    confirmPopup() {
      this.$refs.popup.close();
      this.resolvePromise(true);
    },

    cancelPopup() {
      this.$refs.popup.close();
      this.resolvePromise(false);
    }
  },
  computed: {
    ...mapGetters(["getSatStandard"]),
    ...mapState({
      satContent: state => state.satContent,
      satSavedData: state => state.satSavedData,
      //unsavedChanges: state => state.unsavedChanges
    }),
    unsavedChanges: {
      get() {
        return this.$store.state.unsavedChanges
      },
      set(val) {
        this.$store.commit('setUnsavedChanges', val)
      }
    },
    currentAssessment() {
      return this.satContent && this.satContent.Tools && this.satId
        ? this.satContent.Tools.filter(tool => {
            return tool.Id === this.satId;
          })[0]
        : {};
    },
    currentStandard() {
      let allStandards = [];
      let standard;
      if (
        this.satContent &&
        this.satContent.Tools &&
        this.satId &&
        this.standardId
      ) {
        this.satContent.Tools.forEach(tool => {
          allStandards = allStandards.concat(tool.Standards);
        });
        standard = allStandards.filter(
          standard => standard.Id === this.standardId
        )[0];
      }
      return standard;
    },
    currentStandardIndex() {
      return this.currentAssessment.Standards.findIndex(
        x => x.Id === this.currentStandard.Id
      );
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.unsavedChanges) {
      const answer = this.showPopup();
      // const answer = window.confirm(
      //   "You have not saved changes to the assessment. If you don’t save them before leaving the assessment, you will lose them.\n\nAre you sure you want to leave?"
      // );

      if (answer) {
        return next();
      } else {
        return next(false);
      }
    } else {
      return next();
    }
  }
};
</script>

<style lang="sass" scoped></style>
